/**
 * Compares each checkpoint in updatedChecklists with corresponding checkpoints in originalChecklists.
 * It determines which checkpoints have been modified by comparing their current and original state.
 * Checkpoints are considered unchanged if their serialized (JSON stringified) versions match.
 * The function ignores checkpoints of types 'Attachment' and 'Signature' as these are typically handled differently.
 *
 * @param {PatrolChecklist[]} updatedChecklists - The checklists reflecting the current state, potentially modified from the original.
 * @param {PatrolChecklist[]} originalChecklists - The original checklists as fetched initially or stored previously.
 * @returns {PatrolCheckpoint[]} An array of checkpoints that have been identified as modified, based on the comparison between updated and original data.
 */
export function getUpdatedCheckpoints(
	updatedChecklists: PatrolChecklist[],
	originalChecklists: PatrolChecklist[],
): PatrolCheckpoint[] {
	const updatedCheckpoints: PatrolCheckpoint[] = updatedChecklists.flatMap(
		(currentChecklist) => {
			// Find the matching original checklist by ID
			const originalChecklist = originalChecklists.find(
				(oChecklist) =>
					oChecklist.ChecklistID === currentChecklist.ChecklistID,
			);

			return currentChecklist.Checkpoints.flatMap((currentCheckpoint) => {
				// If the checkpoint is an attachment or signature, skip it. No need to compare these
				if (
					currentCheckpoint.ResponseTypeName === 'Attachment' ||
					currentCheckpoint.ResponseTypeName === 'Signature'
				) {
					return [];
				}

				// Find the matching original checkpoint by ID
				const originalCheckpoint = originalChecklist?.Checkpoints.find(
					(oCheckpoint) =>
						oCheckpoint.CheckpointID ===
						currentCheckpoint.CheckpointID,
				);

				// Compare current checkpoint with the original checkpoint
				if (
					JSON.stringify(originalCheckpoint) !==
					JSON.stringify(currentCheckpoint)
				) {
					// If anything has changed, return the current checkpoint
					return currentCheckpoint;
				}
				// If nothing has changed, return nothing
				return [];
			});
		},
	);
	return updatedCheckpoints;
}
